import { Box, Typography, Button, Menu, MenuItem, IconButton, Dialog, DialogTitle, DialogContent, TextField, DialogActions, DialogContentText, FormControl, InputLabel, Select, Alert, Chip } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState, useMemo } from "react";
import { useTheme } from "@mui/material";

import { useSelector } from "react-redux";
import moment from "moment";
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { 
    useGetEmployeesQuery,
    useGetAllTimeOffRequestsQuery,
    useCreateTimeOffRequestMutation,
    useUpdateTimeOffRequestMutation,
    useDeleteTimeOffRequestMutation
} from "../../state/api";

const TimeRequest = () => {
    const theme = useTheme();
    const isMobileScreen = useMediaQuery('(max-width:768px)');
    
    // Get current user ID from Redux state
    const currentUserId = useSelector((state) => state.global.userId);
    
    const { data: employeesResponse, isLoading: isLoadingEmployees, isError: isErrorEmployees } = useGetEmployeesQuery();
    const { data: timeOffRequestsResponse, isLoading: isLoadingRequests, isError: isErrorRequests } = useGetAllTimeOffRequestsQuery();
    const [createTimeOffRequest] = useCreateTimeOffRequestMutation();
    const [updateTimeOffRequest] = useUpdateTimeOffRequestMutation();
    const [deleteTimeOffRequest] = useDeleteTimeOffRequestMutation();
    
    const employeesData = employeesResponse?.data;
    const allTimeOffRequests = timeOffRequestsResponse || [];
    
    const [selectedFilter, setSelectedFilter] = useState("Pending");
    const filterOptions = ["Pending", "Approved", "Rejected", "All"];
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [editableRequestData, setEditableRequestData] = useState(null);
    const [validationAlert, setValidationAlert] = useState({ show: false, message: '', severity: 'warning' });
    
    const [newRequestData, setNewRequestData] = useState({
        employeeId: "",
        requestType: "Annual Leave",
        startDate: moment().add(14, 'days').format("YYYY-MM-DD"),
        endDate: moment().add(14, 'days').format("YYYY-MM-DD"),
        isAllDay: true,
        startTime: "09:00",
        endTime: "17:00",
        reason: "",
    });



    const handleMenuClick = (event, params) => {
        setAnchorEl(event.currentTarget);
        setSelectedRow(params.row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleViewOpen = () => {
        setOpenViewDialog(true);
        handleMenuClose();
    }

    const handleEditOpen = () => {
        setEditableRequestData({
            ...selectedRow,
            startDate: moment(selectedRow.startDate).format("YYYY-MM-DD"),
            endDate: moment(selectedRow.endDate).format("YYYY-MM-DD"),
            startTime: selectedRow.startTime || "09:00",
            endTime: selectedRow.endTime || "17:00",
        });
        setOpenEditDialog(true);
        handleMenuClose();
    };

    const handleEditSubmit = async () => {
        if (editableRequestData) {
            // Clear previous alerts
            setValidationAlert({ show: false, message: '', severity: 'warning' });
            
            // Comprehensive validation checks
            if (!editableRequestData.requestType || !editableRequestData.startDate || !editableRequestData.endDate) {
                setValidationAlert({ show: true, message: "Please fill in all required fields (Request Type, Start Date, and End Date).", severity: 'error' });
                return;
            }

            // Date validation
            const startDate = moment(editableRequestData.startDate);
            const endDate = moment(editableRequestData.endDate);
            
            if (endDate.isBefore(startDate)) {
                setValidationAlert({ show: true, message: "End date cannot be before start date.", severity: 'error' });
                return;
            }

            // Validate that request is at least 2 weeks in advance (only for future requests)
            const twoWeeksFromNow = moment().add(14, 'days');
            if (startDate.isBefore(twoWeeksFromNow) && startDate.isAfter(moment())) {
                setValidationAlert({ show: true, message: "RESTRICTION: Time-off requests for future dates must be made at least two weeks in advance.", severity: 'warning' });
                return;
            }

            // Check for weekend restrictions for certain types
            if (editableRequestData.requestType === 'Appointment' && (startDate.day() === 0 || startDate.day() === 6)) {
                setValidationAlert({ show: true, message: "RESTRICTION: Medical appointments typically cannot be scheduled on weekends.", severity: 'warning' });
                return;
            }

            // Check for maximum duration restrictions
            const duration = endDate.diff(startDate, 'days') + 1;
            if (editableRequestData.requestType === 'Sick Leave' && duration > 5) {
                setValidationAlert({ show: true, message: "RESTRICTION: Sick leave requests longer than 5 days may require medical documentation.", severity: 'warning' });
                return;
            }

            if (editableRequestData.requestType === 'Annual Leave' && duration > 14) {
                setValidationAlert({ show: true, message: "RESTRICTION: Annual leave requests longer than 14 days require manager approval.", severity: 'warning' });
                return;
            }

            // Check for holiday period restrictions
            const holidayPeriods = [
                { start: moment().month(11).date(20), end: moment().month(11).date(31), name: "Christmas Period" },
                { start: moment().month(0).date(1), end: moment().month(0).date(5), name: "New Year Period" }
            ];

            for (const period of holidayPeriods) {
                if (startDate.isBetween(period.start, period.end, null, '[]') || endDate.isBetween(period.start, period.end, null, '[]')) {
                    setValidationAlert({ show: true, message: `RESTRICTION: Requests during ${period.name} require special approval due to high demand.`, severity: 'warning' });
                    return;
                }
            }

            try {
                const updateData = {
                    ...editableRequestData,
                    startDate: new Date(editableRequestData.startDate).toISOString(),
                    endDate: new Date(editableRequestData.endDate).toISOString(),
                    userId: currentUserId,
                };

                await updateTimeOffRequest({ id: editableRequestData._id, ...updateData });
                setOpenEditDialog(false);
                setValidationAlert({ show: false, message: '', severity: 'warning' });
                // Refresh data
                window.location.reload();
            } catch (error) {
        
                setValidationAlert({ show: true, message: `Failed to update request: ${error.data?.message || error.message}`, severity: 'error' });
            }
        }
    };

    const handleAddDialogOpen = () => {
        setOpenAddDialog(true);
        setValidationAlert({ show: false, message: '', severity: 'warning' });
    };
    
    const handleAddDialogClose = () => {
        setOpenAddDialog(false);
        setValidationAlert({ show: false, message: '', severity: 'warning' });
        setNewRequestData({
            employeeId: "",
            requestType: "Annual Leave",
            startDate: moment().add(14, 'days').format("YYYY-MM-DD"),
            endDate: moment().add(14, 'days').format("YYYY-MM-DD"),
            isAllDay: true,
            startTime: "09:00",
            endTime: "17:00",
            reason: "",
        });
    };

    const handleNewRequestInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewRequestData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    };
    
    const handleAddSubmit = async () => {
        // Clear previous alerts
        setValidationAlert({ show: false, message: '', severity: 'warning' });
        
        // Comprehensive validation checks
        if (!newRequestData.employeeId || !newRequestData.startDate || !newRequestData.endDate) {
            setValidationAlert({ show: true, message: "Please fill in all required fields (Employee, Start Date, and End Date).", severity: 'error' });
            return;
        }

        // Date validation
        const startDate = moment(newRequestData.startDate);
        const endDate = moment(newRequestData.endDate);
        
        if (endDate.isBefore(startDate)) {
            setValidationAlert({ show: true, message: "End date cannot be before start date.", severity: 'error' });
            return;
        }

        // Validate that request is at least 2 weeks in advance
        const twoWeeksFromNow = moment().add(14, 'days');
        if (startDate.isBefore(twoWeeksFromNow)) {
            setValidationAlert({ show: true, message: "RESTRICTION: Time-off requests must be made at least two weeks in advance.", severity: 'error' });
            return;
        }

        // Check for weekend restrictions for certain types
        if (newRequestData.requestType === 'Appointment' && (startDate.day() === 0 || startDate.day() === 6)) {
            setValidationAlert({ show: true, message: "RESTRICTION: Medical appointments typically cannot be scheduled on weekends.", severity: 'warning' });
            return;
        }

        // Check for maximum duration restrictions
        const duration = endDate.diff(startDate, 'days') + 1;
        if (newRequestData.requestType === 'Sick Leave' && duration > 5) {
            setValidationAlert({ show: true, message: "RESTRICTION: Sick leave requests longer than 5 days may require medical documentation.", severity: 'warning' });
            return;
        }

        if (newRequestData.requestType === 'Annual Leave' && duration > 14) {
            setValidationAlert({ show: true, message: "RESTRICTION: Annual leave requests longer than 14 days require manager approval.", severity: 'warning' });
            return;
        }

        // Check for holiday period restrictions
        const holidayPeriods = [
            { start: moment().month(11).date(20), end: moment().month(11).date(31), name: "Christmas Period" },
            { start: moment().month(0).date(1), end: moment().month(0).date(5), name: "New Year Period" }
        ];

        for (const period of holidayPeriods) {
            if (startDate.isBetween(period.start, period.end, null, '[]') || endDate.isBetween(period.start, period.end, null, '[]')) {
                setValidationAlert({ show: true, message: `RESTRICTION: Requests during ${period.name} require special approval due to high demand.`, severity: 'warning' });
                return;
            }
        }
        
        try {
            const requestToCreate = {
                ...newRequestData,
                startDate: new Date(newRequestData.startDate).toISOString(),
                endDate: new Date(newRequestData.endDate).toISOString(),
                userId: currentUserId,
            };

            await createTimeOffRequest(requestToCreate).unwrap();
            handleAddDialogClose();
            // Refresh data
            window.location.reload();
        } catch (error) {
    
            setValidationAlert({ show: true, message: `Failed to create request: ${error.data?.message || error.message}`, severity: 'error' });
        }
    };
    
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditableRequestData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleDeleteOpen = () => {
        setOpenDeleteDialog(true);
        handleMenuClose();
    };

    const handleDelete = async () => {
        if (selectedRow) {
            try {
                await deleteTimeOffRequest({ 
                    id: selectedRow._id, 
                    userId: currentUserId
                });
                setOpenDeleteDialog(false);
                // Refresh data
                window.location.reload();
            } catch (error) {
        
            }
        }
    };

    const handleProcessRequest = async (status, rejectionReason = '') => {
        if (selectedRow) {
            try {
                const updateData = {
                    status,
                    approvedBy: currentUserId,
                    rejectionReason: status === 'Rejected' ? rejectionReason : undefined,
                    userId: currentUserId,
                };

                await updateTimeOffRequest({ id: selectedRow._id, ...updateData });
                handleMenuClose();
                // Refresh data
                window.location.reload();
            } catch (error) {
        
            }
        }
    };

    const processedData = useMemo(() => {
        if (!allTimeOffRequests || !employeesData || isLoadingRequests || isLoadingEmployees) {
            return [];
        }

        if (!allTimeOffRequests.length) return [];

        return allTimeOffRequests.map(request => {
            let employeeName = 'Unknown Employee';
            let approvedByName = '';
            
            // Check if employeeId is populated (is an object) or just an ID string
            if (request.employeeId && typeof request.employeeId === 'object') {
                const { firstName, lastName } = request.employeeId.names;
                employeeName = `${firstName} ${lastName}`;
            } else {
                // Fallback for non-populated ID
                const employee = employeesData.find(emp => emp._id === request.employeeId);
                if (employee) {
                    employeeName = `${employee.names.firstName} ${employee.names.lastName}`;
                }
            }
            
            // Handle approvedBy field - check if it's populated
            if (request.approvedBy && typeof request.approvedBy === 'object') {
                approvedByName = `${request.approvedBy.firstName} ${request.approvedBy.lastName}`;
            }
            
            return {
                ...request,
                id: request._id,
                employeeName,
                approvedByName,
                displayStartDate: moment(request.startDate).format("DD/MM/YYYY"),
                displayEndDate: moment(request.endDate).format("DD/MM/YYYY"),
                displayRequestedAt: moment(request.requestedAt).format("DD/MM/YYYY"),
                duration: moment(request.endDate).diff(moment(request.startDate), 'days') + 1,
            };
        }).filter(request => {
            if (selectedFilter === "All") return true;
            return request.status === selectedFilter;
        });
    }, [timeOffRequestsResponse, employeesResponse, selectedFilter, isLoadingRequests, isLoadingEmployees]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'Pending':
                return theme.palette.warning.main;
            case 'Approved':
                return theme.palette.success.main;
            case 'Rejected':
                return theme.palette.error.main;
            default:
                return theme.palette.grey[500];
        }
    };

    const columns = [
        {
            field: "profilePic",
            headerName: "",
            width: 80,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="50%"
                    width="50px"
                    height="50px"
                    backgroundColor={theme.palette.primary.light}
                    sx={{ml: 'auto', mr: 'auto'}}
                >
                    <Typography fontWeight="bold" variant='h6' color={theme.palette.secondary[300]}>
                        {params.row.employeeName.split(" ").map(n => n[0]).join("").toUpperCase()}
                    </Typography>
                </Box>
            ),
        },
        { field: "employeeName", headerName: "Employee", flex: 1, cellClassName: "name-column--cell" },
        { field: "requestType", headerName: "Type", flex: 1 },
        { field: "displayStartDate", headerName: "Start Date", flex: 1 },
        { field: "displayEndDate", headerName: "End Date", flex: 1 },
        { field: "duration", headerName: "Duration (days)", flex: 0.8 },
        { 
            field: "status", 
            headerName: "Status", 
            flex: 1,
            renderCell: (params) => (
                <Chip
                    label={params.row.status}
                    sx={{
                        backgroundColor: getStatusColor(params.row.status),
                        color: 'white',
                        fontWeight: 'bold'
                    }}
                />
            )
        },
        { 
            field: "approvedByName", 
            headerName: "Processed By", 
            flex: 1,
            renderCell: (params) => (
                <Typography>
                    {params.row.status !== 'Pending' && params.row.approvedByName 
                        ? params.row.approvedByName 
                        : params.row.status === 'Pending' ? '-' : 'Unknown'
                    }
                </Typography>
            )
        },
        { field: "displayRequestedAt", headerName: "Requested", flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            flex: 0.4,
            renderCell: (params) => {
                const isMenuOpen = Boolean(anchorEl) && selectedRow && selectedRow._id === params.row._id;
                return (
                    <Box>
                        <IconButton onClick={(e) => handleMenuClick(e, params)}>
                            <MoreVertIcon />
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            open={isMenuOpen}
                            onClose={handleMenuClose}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                        >
                            {[
                                <MenuItem key="view" onClick={handleViewOpen}>View</MenuItem>,
                                <MenuItem key="edit" onClick={handleEditOpen}>Edit</MenuItem>,
                                <MenuItem key="delete" onClick={handleDeleteOpen}>Delete</MenuItem>,
                                ...(selectedRow?.status === 'Pending' ? [
                                    <MenuItem key="approve" onClick={() => handleProcessRequest('Approved')}>Approve</MenuItem>,
                                    <MenuItem key="reject" onClick={() => handleProcessRequest('Rejected', 'Rejected by admin')}>Reject</MenuItem>
                                ] : [])
                            ]}
                        </Menu>
                    </Box>
                );
            },
        }
    ];

    return (
        <Box>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                m="1.5rem 2.5rem"
            >
                 <Typography variant="h4" color={theme.palette.secondary[100]}>
                    Time Off Requests
                </Typography>
                
                <Button
                    onClick={handleAddDialogOpen}
                    sx={{
                        backgroundColor: theme.palette.secondary.main,
                        color: theme.palette.background.alt,
                        fontWeight: 'bold',
                        padding: '10px 20px',
                        '&:hover': {
                            backgroundColor: theme.palette.secondary.dark
                        }
                    }}
                >
                    Request Time Off
                </Button>
            </Box>

            {/* Filter Buttons */}
            <Box display="flex" gap="10px" m="1.5rem 2.5rem">
                {filterOptions.map((option) => (
                    <Button
                        key={option}
                        variant={selectedFilter === option ? "contained" : "outlined"}
                        onClick={() => setSelectedFilter(option)}
                        sx={{
                            backgroundColor: selectedFilter === option ? theme.palette.secondary.light : "transparent",
                            color: selectedFilter === option ? theme.palette.background.alt : theme.palette.secondary[100],
                            borderColor: theme.palette.secondary[100],
                        }}
                    >
                        {option}
                    </Button>
                ))}
            </Box>

            {/* Time Off Requests Data Grid */}
            <Box
                m="1.5rem 2.5rem"
                height="calc(76vh - 40px)"
                sx={{
                    "& .MuiDataGrid-root": { border: "none", backgroundColor: "transparent" },
                    "& .MuiDataGrid-row": {
                        borderBottom: `1px solid ${theme.palette.secondary[300]}`,
                        borderRadius: "1px", cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: theme.palette.secondary.light
                        }
                    },
                    "& .MuiDataGrid-cell": { borderBottom: "none" },
                    "& .name-column--cell": { color: theme.palette.secondary[200], fontWeight: "bold" },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: theme.palette.primary.light,
                        borderBottom: `1px solid ${theme.palette.secondary[300]}`,
                        color: theme.palette.secondary[100],
                    },
                    "& .MuiDataGrid-virtualScroller": { backgroundColor: theme.palette.background.alt },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: `1px solid ${theme.palette.secondary[300]}`,
                        backgroundColor: theme.palette.primary.light,
                        color: theme.palette.secondary[100],
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": { color: `${theme.palette.secondary[200]} !important`, fontSize: "16px" },
                }}
            >
                {(isLoadingRequests || isLoadingEmployees) ? (
                    <Typography sx={{textAlign: 'center', mt: 5}}>Loading time-off requests...</Typography>
                ) : (isErrorEmployees || isErrorRequests) ? (
                    <Typography sx={{textAlign: 'center', mt: 5, color: 'red'}}>Error: Could not load data.</Typography>
                ) : (processedData && processedData.length > 0) ? (
                    <DataGrid
                        loading={isLoadingRequests || isLoadingEmployees}
                        rows={processedData || []}
                        columns={columns}
                        slots={{ toolbar: GridToolbar }}
                        rowHeight={isMobileScreen ? 80 : 100}
                    />
                ) : (
                    <Typography sx={{textAlign: 'center', mt: 5}}>
                        {selectedFilter === "All" 
                            ? "No time-off requests found." 
                            : `No ${selectedFilter.toLowerCase()} requests found.`
                        }
                    </Typography>
                )}
            </Box>

            {/* Delete Dialog */}
            <Dialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
            >
                <DialogTitle>CONFIRM DELETION</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this time-off request for {selectedRow?.employeeName}? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleDelete}
                        variant="contained"
                        color="secondary"
                        sx={{
                            backgroundColor: theme.palette.error.main,
                        }}
                    >
                        Delete
                    </Button>
                    <Button
                        onClick={() => setOpenDeleteDialog(false)}
                        variant="contained"
                        color="secondary"
                        sx={{
                            backgroundColor: theme.palette.secondary.main,
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Dialog */}
            <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} fullWidth maxWidth="sm">
                <DialogTitle>TIME OFF REQUEST DETAILS</DialogTitle>
                <DialogContent>
                    {selectedRow && (
                        <Box>
                            <Typography><strong>Employee:</strong> {selectedRow.employeeName}</Typography>
                            <Typography><strong>Type:</strong> {selectedRow.requestType}</Typography>
                            <Typography><strong>Start Date:</strong> {selectedRow.displayStartDate}</Typography>
                            <Typography><strong>End Date:</strong> {selectedRow.displayEndDate}</Typography>
                            <Typography><strong>Duration:</strong> {selectedRow.duration} days</Typography>
                            <Typography><strong>All Day:</strong> {selectedRow.isAllDay ? 'Yes' : 'No'}</Typography>
                            {!selectedRow.isAllDay && (
                                <>
                                    <Typography><strong>Start Time:</strong> {selectedRow.startTime}</Typography>
                                    <Typography><strong>End Time:</strong> {selectedRow.endTime}</Typography>
                                </>
                            )}
                            <Typography><strong>Reason:</strong> {selectedRow.reason || 'No reason provided'}</Typography>
                            <Typography><strong>Status:</strong> {selectedRow.status}</Typography>
                            {selectedRow.status !== 'Pending' && selectedRow.approvedByName && (
                                <Typography><strong>Processed By:</strong> {selectedRow.approvedByName}</Typography>
                            )}
                            {selectedRow.processedAt && (
                                <Typography><strong>Processed Date:</strong> {moment(selectedRow.processedAt).format("DD/MM/YYYY HH:mm")}</Typography>
                            )}
                            <Typography><strong>Requested:</strong> {selectedRow.displayRequestedAt}</Typography>
                            {selectedRow.rejectionReason && (
                                <Typography><strong>Rejection Reason:</strong> {selectedRow.rejectionReason}</Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                    onClick={() => setOpenViewDialog(false)}
                        variant="contained"
                        color="secondary"
                        sx={{
                            backgroundColor: theme.palette.secondary.main,
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={openEditDialog} onClose={() => {
                setOpenEditDialog(false);
                setValidationAlert({ show: false, message: '', severity: 'warning' });
            }} fullWidth maxWidth="sm">
                <DialogTitle>EDIT TIME OFF REQUEST</DialogTitle>
                <DialogContent>
                    {validationAlert.show && (
                        <Alert severity={validationAlert.severity} sx={{ mb: 2 }}>
                            {validationAlert.message}
                        </Alert>
                    )}
                    <FormControl fullWidth margin="dense" variant="standard">
                        <InputLabel>Request Type</InputLabel>
                        <Select
                            name="requestType"
                            value={editableRequestData?.requestType || ''}
                            onChange={handleInputChange}
                            label="Request Type"
                        >
                            <MenuItem value="Annual Leave">Annual Leave</MenuItem>
                            <MenuItem value="Sick Leave">Sick Leave</MenuItem>
                            <MenuItem value="Appointment">Appointment</MenuItem>
                            <MenuItem value="Holiday">Holiday</MenuItem>
                            <MenuItem value="unPaid Holiday">Unpaid Holiday</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        margin="dense"
                        label="Start Date"
                        name="startDate"
                        type="date"
                        value={editableRequestData?.startDate || ''}
                        onChange={handleInputChange}
                        fullWidth
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        margin="dense"
                        label="End Date"
                        name="endDate"
                        type="date"
                        value={editableRequestData?.endDate || ''}
                        onChange={handleInputChange}
                        fullWidth
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        margin="dense"
                        label="Reason"
                        name="reason"
                        multiline
                        rows={3}
                        value={editableRequestData?.reason || ''}
                        onChange={handleInputChange}
                        fullWidth
                        variant="standard"
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                    onClick={() => {
                        setOpenEditDialog(false);
                        setValidationAlert({ show: false, message: '', severity: 'warning' });
                    }}
                        variant="contained"
                        color="secondary"
                        sx={{
                            backgroundColor: theme.palette.error.main,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                    onClick={handleEditSubmit}
                        variant="contained"
                        color="secondary"
                        sx={{
                            backgroundColor: theme.palette.secondary.main,
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Request Dialog */}
            <Dialog open={openAddDialog} onClose={handleAddDialogClose} fullWidth maxWidth="sm">
                <DialogTitle textTransform="uppercase">Request Time Off</DialogTitle>
                <DialogContent>
                    {validationAlert.show && (
                        <Alert severity={validationAlert.severity} sx={{ mb: 2 }}>
                            {validationAlert.message}
                        </Alert>
                    )}
                    <FormControl fullWidth margin="dense" variant="standard">
                        <InputLabel>Employee*</InputLabel>
                        <Select
                            name="employeeId"
                            value={newRequestData.employeeId}
                            onChange={handleNewRequestInputChange}
                            label="Employee"
                        >
                            {employeesData?.map((employee) => (
                                <MenuItem key={employee._id} value={employee._id}>
                                    {employee.names.firstName} {employee.names.lastName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth margin="dense" variant="standard">
                        <InputLabel>Request Type*</InputLabel>
                        <Select
                            name="requestType"
                            value={newRequestData.requestType}
                            onChange={handleNewRequestInputChange}
                            label="Request Type"
                        >
                            <MenuItem value="Annual Leave">Annual Leave</MenuItem>
                            <MenuItem value="Sick Leave">Sick Leave</MenuItem>
                            <MenuItem value="Appointment">Appointment</MenuItem>
                            <MenuItem value="Holiday">Holiday</MenuItem>
                            <MenuItem value="unPaid Holiday">Unpaid Holiday</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        margin="dense"
                        label="Start Date*"
                        name="startDate"
                        type="date"
                        value={newRequestData.startDate}
                        onChange={handleNewRequestInputChange}
                        fullWidth
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        margin="dense"
                        label="End Date*"
                        name="endDate"
                        type="date"
                        value={newRequestData.endDate}
                        onChange={handleNewRequestInputChange}
                        fullWidth
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        margin="dense"
                        label="Reason"
                        name="reason"
                        multiline
                        rows={3}
                        value={newRequestData.reason}
                        onChange={handleNewRequestInputChange}
                        fullWidth
                        variant="standard"
                        placeholder="Optional reason for the request..."
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleAddDialogClose}
                            variant="contained"
                            color="secondary"
                            sx={{
                                backgroundColor: theme.palette.error.main,
                            }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddSubmit}
                            variant="contained"
                            color="secondary"
                            sx={{
                                backgroundColor: theme.palette.secondary.main,
                            }}
                    >
                        Submit Request
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TimeRequest;
